<template>
  <div
    :style="{ '--min-content-height': `${minContentHeight}px` }"
    class="product-description">
    <div
      v-if="showNavigation"
      class="product-description__nav">
      <LinearTabs
        :value="currentTabIndex"
        :tab-list="tabList"
        @input="scrollToCurrentBlock" />
    </div>

    <div
      ref="descriptionBody"
      class="product-description__body">
      <div
        v-for="(tab, id) in tabList"
        :ref="`content-${id}`"
        :key="`content-${tab.name}`"
        class="product-description__item">
        <h2 class="product-description__item-title">
          {{ tab.name }}
        </h2>

        <component
          :is="tab.component"
          v-if="tab.component"
          :product="product"
          class="product-description__component"
          v-bind="tab.componentProps" />

        <template v-else-if="tab.html">
          <div
            class="product-description__wrapper">
            <div
              :ref="`tab-html-${id}`"
              class="product-description__html product-description__html--full"
              v-html="tab.html" />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import GameModel from 'Models/GameModel';
import ReviewsSlider from 'Components/Reviews/ReviewsSlider.vue';
import tooltips from 'Root/mixins/tooltips';
import LinearTabs from 'Components/ui/Tabs/LinearTabs.vue';
import { regExpWowHeadLink, regExpLightGGLink } from 'Root/helpers/regexps';
import { executeAfterLoad, scrollTo } from 'Root/helpers/tools';
import { decorateTables } from 'Root/helpers/seo';
import { htmlNormalizeImageRatio } from '../../../helpers/htmlNormalizer';

const TAB_MOBILE_MAX_HEIGHT = 145;

export default {
  name: 'ProductDescription',
  components: { LinearTabs },
  mixins: [
    tooltips,
  ],

  props: {
    product: {
      type: Object,
      required: true,
    },

    showNavigation: {
      type: Boolean,
      default: false,
    },

    reviewsSliderProps: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    return {
      currentTabIndex: 0,
      TAB_MOBILE_MAX_HEIGHT,
      minContentHeight: 90,
    };
  },

  computed: {
    ...mapState(['activeGame', 'reviews']),

    tabList() {
      const tabContents = [];

      if (this.product.additional_info?.product_contains) {
        tabContents.push({
          name: this.product.additional_info.product_contains_title || 'What you\'ll get',
          html: this.cleanHTML(this.product.additional_info.product_contains),
          id: 0,
        });
      }

      if (this.product.additional_info?.description) {
        tabContents.push({
          name: this.product.additional_info.description_title || 'Requirements',
          html: this.cleanHTML(this.product.additional_info.description),
          id: 1,
        });
      }

      if (this.product.additional_info?.service_included) {
        tabContents.push({
          name: this.product.additional_info.service_included_title || 'More info',
          html: this.cleanHTML(this.product.additional_info.service_included),
          id: 2,
        });
      }

      const reviews = this.product.product_reviews?.length
        ? this.product.product_reviews
        : this.reviews;

      const componentDefaultProps = {
        reviews,
        desktopBreakPoint: 1300,
        breakpoints: {
          1700: {
            perView: 2,
          },
        },
        perView: 3,
        propsForReviewItem: {
          isProductReview: true,
          gameId: this.activeGame.id,
        },
      };

      if (reviews?.length) {
        tabContents.push({
          name: 'Reviews',
          component: ReviewsSlider,
          componentProps: {
            ...componentDefaultProps,
            ...this.reviewsSliderProps,
          },
          id: 3,
        });
      }

      return tabContents;
    },
  },

  mounted() {
    this.$nextTick(() => {
      const orderAreaHeight = document.querySelector('.game-offer__order')?.offsetHeight;
      if (orderAreaHeight) {
        // 565 подобрано вручную, чтобы при отсутствии сложностей minContentHeight был равен 90 (значение по умолчанию)
        this.minContentHeight = orderAreaHeight - 565;
      }
    });

    decorateTables(this.$refs.descriptionBody);
  },

  methods: {
    cleanHTML(html) {
      if (!html) {
        return null;
      }

      let cleanedHtml = html;

      if (GameModel.LIST_WOW_ALL_IDS.includes(this.activeGame.id)) {
        cleanedHtml = cleanedHtml.replace(
          regExpWowHeadLink,
          'href="#" data-wowhead="$3&domain=$2" onclick="return false"',
        );
      } else if (this.activeGame.id === GameModel.DESTINY.id) {
        let matchedUrls = cleanedHtml.match(regExpLightGGLink);

        if (matchedUrls) {
          matchedUrls = matchedUrls.map((url) => ({
            key: url.slice(0, -1), // ключ в объекте кэша (равен href)
            link: url.replace(regExpLightGGLink, '$2hover/$4'), // прямая ссылка на тултип
          }));
        }

        cleanedHtml = cleanedHtml.replace(regExpLightGGLink, '$1" onclick="return false" rel="nofollow noopener"');

        if (matchedUrls) {
          executeAfterLoad(() => {
            requestAnimationFrame(() => {
              if (window?.preloadTooltipByUrl) {
                this.preloadDestinyTooltipsFromUrls(matchedUrls);
              } else {
                const tooltipScript = document.getElementById('destiny-tooltips');
                tooltipScript.onload = () => {
                  this.preloadDestinyTooltipsFromUrls(matchedUrls);
                };
              }
            });
          });
        }
      }

      cleanedHtml = htmlNormalizeImageRatio(cleanedHtml);

      return cleanedHtml;
    },

    preloadDestinyTooltipsFromUrls(items) {
      items.forEach(({ key, link }) => {
        window.preloadTooltipByUrl(key, link);
      });
    },

    scrollToCurrentBlock(index) {
      const { top } = this.$refs[`content-${index}`][0].getBoundingClientRect();
      const y = top + window.pageYOffset - 30;
      scrollTo(0, y);
    },
  },
};
</script>

<style lang="scss">
  .product-description__body {
    /* ставим дефолтный цвет ссылок */
    a {
      color: $color--basic-blue;
    }

    /* ставим дефолтный цвет ссылки для wowhead */
    a[data-wowhead] {
      color: #ffd100 !important;
    }
  }
</style>

<style lang="scss" scoped>
  .product-description {
    --min-content-height: 90px;

    line-height: 1.2;
  }

  .product-description__nav {
    display: none;
  }

  .product-description__item {
    margin-bottom: 32px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .product-description__item-title {
    font-weight: 500;
    font-size: 17px;
    margin-bottom: 16px;
  }

  .product-description__read-more {
    position: absolute;
    bottom: -20px;
    left: calc(50% - 58px);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 116px;
    height: 32px;
    padding: 0;
    z-index: 2;
  }

  .product-description__component {
    margin-bottom: 30px;
  }

  .product-description__wrapper {
    position: relative;
  }

  .product-description__html {
    --tab-mobile-max-height: 145px;

    max-height: var(--tab-mobile-max-height);
    overflow: hidden;
    margin-bottom: 43px;
    padding-top: 5px;

    &:before {
      content: '';
      position: absolute;
      bottom: -1px;
      left: 0;
      height: 60px;
      width: 100%;
      background: linear-gradient(180deg, rgba($color--basic-black, 0) 0%, $color--basic-black 100%);
      z-index: 1;
    }
  }

  .product-description__html--full {
    max-height: 100%;
    margin-bottom: 30px;

    &:before {
      display: none;
    }

    + .product-description__read-more {
      display: none;
    }
  }

  @media screen and #{$screen-less-than-1280} {
    .product-description__item {
      padding: 0 var(--padding);
    }

    /deep/.product-description__component {
      padding: 0;

      .reviews__body {
        scroll-padding-left: var(--padding);
        padding: 0 var(--padding);
        margin: 0 calc(var(--padding) * -1);
      }
    }
  }

  @media screen and #{$screen-more-or-1280} {
    .product-description__nav {
      display: flex;
      border-bottom: 1px solid $color--basic-dark-gray;
      margin-bottom: 32px;
    }

    .product-description__nav-item {
      position: relative;
      font-size: 19px;
      color: $color--basic-dark-gray;
      padding-bottom: 13px;
      transition: var(--transition-color);
      cursor: pointer;
      margin-right: 32px;
      font-weight: 500;

      &:last-child {
        margin-right: 0;
      }

      &:after {
        content: '';
        position: absolute;
        left: 0;
        bottom: -3px;
        height: 5px;
        width: 100%;
        background-color: transparent;
        transition: background-color .2s ease-out;
      }

      &:hover {
        color: $color--basic-light-gray;
      }
    }

    .product-description__nav-item--active {
        color: $color--basic-white;

        &:after {
          background-color: $color--basic-blue;
        }
      }

    .product-description__item-title {
      font-size: 25px;
      margin-bottom: 24px;
    }

    .product-description__component {
      margin-right: 0;
      margin-bottom: 0;
      padding-bottom: 30px;
    }

    .product-description__html {
      max-height: 100%;
      margin-bottom: 0;

      &:before {
        display: none;
      }

      + .product-description__read-more {
        display: none;
      }
    }

    .product-description__body {
      min-height: var(--min-content-height, 90px);
    }
  }

  /deep/.product-description__html {
    @import "../../../../scss/textBase";
    @import "../../../../scss/shapes/arrowhead";

    p {
      img {
        height: auto;
      }
    }
  }
</style>
